import React, { useContext, useMemo } from 'react';
import { Spin } from 'antd';
import { Empty, Modal } from '@maxtropy/components';
import {
  apiV2GasMonitorMapStationAreaDataPost,
  V2GasMonitorMapStationAreaDataPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { ShareDataWrapperContext } from '@/pages/WgqAllMaps';
import { keepOnePercentOrLine } from '@/utils/utils';
import outerStyles from '../index.module.scss';
import styles from './index.module.scss';

interface StationGasOverviewModalProps {
  open: boolean;
  stationName?: string;
  zoneName?: string;
  onClose: () => void;
}

type StationData = NonNullable<V2GasMonitorMapStationAreaDataPostResponse['stationDataList']>[number];

const formatResilience = (value?: number) => {
  if (value === undefined || value === null) return '--';
  return `${keepOnePercentOrLine(value > 1 ? value / 100 : value)}%`;
};

const formatUsage = (value?: number) => (value === undefined || value === null ? '--' : Number(value).toFixed(0));

const StationGasOverviewModal: React.FC<StationGasOverviewModalProps> = ({ open, stationName, zoneName, onClose }) => {
  const { isFullScreen, scale } = useContext(ShareDataWrapperContext);
  const { data, loading } = useRequest(() => apiV2GasMonitorMapStationAreaDataPost({ zoneName }), {
    ready: open && !!zoneName,
    refreshDeps: [open, zoneName],
  });

  const stationDataList = useMemo<StationData[]>(() => data?.stationDataList ?? [], [data]);
  const displayUnit = data?.unit ?? '-';

  return (
    <Modal
      title={stationName || '气体站房'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      mask={false}
      destroyOnClose
      centered
      style={{ top: isFullScreen ? 80 * (scale ?? 1) : 110 * (scale ?? 1) }}
      className={`${outerStyles.customModal} ${styles.stationModal}`}
      getContainer={() => document.getElementById('full_screen_box') ?? document.body}
    >
      <Spin spinning={loading}>
        {stationDataList.length === 0 ? (
          <Empty style={{ height: 420, paddingTop: 150 }} />
        ) : (
          <div className={styles.stationList}>
            {stationDataList.map((station, index) => (
              <section key={`${station.name ?? 'station'}-${index}`} className={styles.stationCard}>
                <div className={styles.stationName}>{station.name ?? '--'}</div>
                <div className={styles.stationMetrics}>
                  <div>
                    <span>今日用量：</span>
                    <span className={styles.meterValue}>{formatUsage(station.todayData)}</span>
                    <span className={styles.meterValue}>{displayUnit}</span>
                  </div>
                  <div>
                    <span>实时供能韧性：</span>
                    <span className={styles.meterValue}>{formatResilience(station.resilience)}</span>
                  </div>
                  <div>
                    <span>本月用量：</span>
                    <span className={styles.meterValue}>{formatUsage(station.monthData)}</span>
                    <span className={styles.meterValue}>{displayUnit}</span>
                  </div>
                </div>
              </section>
            ))}
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default StationGasOverviewModal;
